import { useEffect, useMemo, useRef, useState } from 'react';
import type { DemandSeries } from '../lib/demandSeries';
import { formatDowMonthDay, formatLongDate, meterDateKey } from '../lib/demandSeries';
import {
  APP_PALETTE,
  layoutDemandYear,
  layoutPeakDay,
  type ChartLayout,
  type Primitive,
} from '../lib/demandChartLayout';

/**
 * The daily-peak chart on the Existing Load tab, and the peak-day detail.
 *
 * Drawn from `layoutDemandYear`, the same geometry the report prints, with a
 * hover layer on top: a crosshair that snaps to the nearest day and a tooltip.
 * Keyboard: focus the chart and use the arrow keys; Home jumps to the peak.
 *
 * `unlocked` is the paywall. Before unlock no kilowatt value is rendered here —
 * not on the axis, not on the marker, not in the tooltip.
 */

function useWidth<T extends HTMLElement>(fallback: number): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth || fallback);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, width];
}

function Prim({ p }: { p: Primitive }) {
  switch (p.kind) {
    case 'rect':
      return <rect x={p.x} y={p.y} width={p.width} height={p.height} fill={p.fill} />;
    case 'line':
      return <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} stroke={p.stroke} strokeWidth={p.strokeWidth} strokeLinecap={p.linecap} />;
    case 'path':
      return (
        <path
          d={p.d}
          fill={p.fill}
          stroke={p.stroke}
          strokeWidth={p.strokeWidth}
          strokeLinejoin={p.stroke ? 'round' : undefined}
          strokeLinecap={p.stroke ? 'round' : undefined}
          transform={p.transform}
        />
      );
    case 'circle':
      return <circle cx={p.cx} cy={p.cy} r={p.r} fill={p.fill} stroke={p.stroke} strokeWidth={p.strokeWidth} />;
    case 'text':
      return (
        <text
          x={p.x}
          y={p.y}
          fill={p.fill}
          fontSize={p.fontSize}
          fontWeight={p.fontWeight}
          textAnchor={p.anchor}
          letterSpacing={p.letterSpacing}
          style={p.tabular ? { fontVariantNumeric: 'tabular-nums' } : undefined}
        >
          {p.text}
        </text>
      );
  }
}

function Tooltip({ layout, x, y, lines }: { layout: ChartLayout; x: number; y: number; lines: [string, string] }) {
  // Positioned in CSS pixels; the SVG scales with its container, so map from
  // the layout's coordinate space through the rendered width.
  return (
    <div
      className="pointer-events-none absolute z-10 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg whitespace-nowrap"
      style={{ left: `${(x / layout.width) * 100}%`, top: `${(y / layout.height) * 100}%`, transform: 'translate(-50%, calc(-100% - 12px))' }}
      role="status"
    >
      <span className="block text-sm font-semibold">{lines[0]}</span>
      <span className="block text-gray-300">{lines[1]}</span>
    </div>
  );
}

interface YearProps {
  series: DemandSeries;
  unlocked: boolean;
  /** Windows the contractor excluded (lib/peakExclusions.ts), shaded amber.
   *  The series is built from the readings in effect, so an excluded day has
   *  no point of its own; the band marks where it was. */
  excluded?: Array<{ start: string; end: string }>;
}


/** Fractional day-index span a window covers, between the days either side of it. */
function excludedSpan(series: DemandSeries, w: { start: string; end: string }): [number, number] | null {
  const s = meterDateKey(Date.parse(w.start));
  const e = meterDateKey(Date.parse(w.end) - 1);
  const days = series.days;
  let before = -1;
  let after = days.length;
  for (let i = 0; i < days.length; i++) {
    if (days[i].date < s) before = i;
    if (days[i].date > e) { after = i; break; }
  }
  const lo = Math.max(0, before + 0.5);
  const hi = Math.min(days.length - 1, after - 0.5);
  return hi >= lo ? [lo, hi] : null;
}

export function DemandYearChart({ series, unlocked, excluded = [] }: YearProps) {
  const [ref, width] = useWidth<HTMLDivElement>(800);
  const layout = useMemo(() => layoutDemandYear(series, { width, unlocked, palette: APP_PALETTE }), [series, width, unlocked]);
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const days = series.days;
  const N = days.length;
  const multiYear = days[0].date.slice(0, 4) !== days[N - 1].date.slice(0, 4);

  function indexFromClientX(clientX: number): number {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    const mx = ((clientX - rect.left) / rect.width) * layout.width;
    const span = layout.plot.right - layout.plot.left;
    const i = Math.round(((mx - layout.plot.left) / span) * (N - 1));
    return Math.max(0, Math.min(N - 1, i));
  }

  const label = unlocked
    ? `Daily peak demand. Maximum ${series.peak.kw.toFixed(2)} kW on ${formatLongDate(series.peak.date)} at ${series.peak.timeLabel}.`
    : `Daily peak demand. Maximum on ${formatLongDate(series.peak.date)} at ${series.peak.timeLabel}, in ${series.peak.season.toLowerCase()}. Values are shown after unlock.`;

  let tip: { x: number; y: number; lines: [string, string] } | null = null;
  if (hover !== null) {
    const d = days[hover];
    const isPeak = hover === series.peakIndex;
    const when = formatDowMonthDay(d.date) + (multiYear ? `, ${d.date.slice(0, 4)}` : '');
    tip = unlocked
      ? { x: layout.x(hover), y: layout.y(d.max), lines: [`${d.max.toFixed(2)} kW`, `${when}${isPeak ? ' · period’s peak' : ''} · ${d.n} readings`] }
      : { x: layout.x(hover), y: layout.y(d.max), lines: [isPeak ? 'Peak day' : when, `${isPeak ? when + ' · ' : ''}${d.n} readings`] };
  }

  return (
    <div ref={ref} className="relative w-full" data-testid="demand-year-chart" data-unlocked={unlocked ? 'true' : 'false'}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        className="block h-auto w-full overflow-visible text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
        role="img"
        aria-label={label}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'ArrowRight') { e.preventDefault(); setHover(h => (h === null ? series.peakIndex : Math.min(N - 1, h + 1))); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); setHover(h => (h === null ? series.peakIndex : Math.max(0, h - 1))); }
          else if (e.key === 'Home') { e.preventDefault(); setHover(series.peakIndex); }
          else if (e.key === 'Escape') { setHover(null); }
        }}
        onBlur={() => setHover(null)}
      >
        {layout.primitives.map((p, i) => <Prim key={i} p={p} />)}
        {excluded.map((w, i) => {
          const span = excludedSpan(series, w);
          if (!span) return null;
          const x1 = layout.x(span[0]);
          const x2 = layout.x(span[1]);
          return (
            <rect
              key={`ex-${i}`}
              data-testid="chart-excluded-band"
              x={x1 - 1.5}
              y={layout.plot.top}
              width={Math.max(3, x2 - x1 + 3)}
              height={layout.plot.bottom - layout.plot.top}
              fill="#f59e0b"
              fillOpacity={0.25}
            >
              <title>Excluded readings</title>
            </rect>
          );
        })}
        {hover !== null && (
          <>
            <line x1={layout.x(hover)} x2={layout.x(hover)} y1={layout.plot.top} y2={layout.plot.bottom} stroke="#9ca3af" strokeWidth={1} />
            <circle cx={layout.x(hover)} cy={layout.y(days[hover].max)} r={4} fill={APP_PALETTE.line} stroke="#fff" strokeWidth={2} />
          </>
        )}
        <rect
          x={layout.plot.left}
          y={layout.plot.top - 18}
          width={layout.plot.right - layout.plot.left}
          height={layout.height - layout.plot.top + 18}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onPointerMove={e => setHover(indexFromClientX(e.clientX))}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      {tip && <Tooltip layout={layout} x={tip.x} y={tip.y} lines={tip.lines} />}
    </div>
  );
}

export function PeakDayChart({ series }: { series: DemandSeries }) {
  const [ref, width] = useWidth<HTMLDivElement>(520);
  const layout = useMemo(() => layoutPeakDay(series, { width, palette: APP_PALETTE }), [series, width]);
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const pts = series.peakDay;

  function indexFromClientX(clientX: number): number {
    const svg = svgRef.current;
    if (!svg || pts.length === 0) return 0;
    const rect = svg.getBoundingClientRect();
    const mx = ((clientX - rect.left) / rect.width) * layout.width;
    const h = ((mx - layout.plot.left) / (layout.plot.right - layout.plot.left)) * 24;
    let best = 0;
    let bd = Infinity;
    pts.forEach((p, i) => { const d = Math.abs(p.hour - h); if (d < bd) { bd = d; best = i; } });
    return best;
  }

  function hourLabel(h: number): string {
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    const ap = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${mm < 10 ? '0' : ''}${mm} ${ap}`;
  }

  const tip = hover !== null && pts[hover]
    ? { x: layout.x(pts[hover].hour), y: layout.y(pts[hover].kw), lines: [`${pts[hover].kw.toFixed(2)} kW`, `${hourLabel(pts[hover].hour)} interval`] as [string, string] }
    : null;

  return (
    <div ref={ref} className="relative w-full" data-testid="demand-peak-day-chart">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        className="block h-auto w-full overflow-visible text-xs"
        role="img"
        aria-label={`Demand on ${formatLongDate(series.peak.date)} in ${series.intervalMinutes}-minute intervals. Peak ${series.peak.kw.toFixed(2)} kW at ${series.peak.timeLabel}.`}
      >
        {layout.primitives.map((p, i) => <Prim key={i} p={p} />)}
        {tip && (
          <>
            <line x1={tip.x} x2={tip.x} y1={layout.plot.top} y2={layout.plot.bottom} stroke="#9ca3af" strokeWidth={1} />
            <circle cx={tip.x} cy={tip.y} r={4} fill={APP_PALETTE.line} stroke="#fff" strokeWidth={2} />
          </>
        )}
        <rect
          x={layout.plot.left}
          y={layout.plot.top}
          width={layout.plot.right - layout.plot.left}
          height={layout.height - layout.plot.top}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onPointerMove={e => setHover(indexFromClientX(e.clientX))}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      {tip && <Tooltip layout={layout} x={tip.x} y={tip.y} lines={tip.lines} />}
    </div>
  );
}

/**
 * The chart's table twin: one row per month with its peak day. The kilowatt
 * column exists only after unlock, for the same reason the axis does.
 */
export function DemandMonthTable({ series, unlocked }: { series: DemandSeries; unlocked: boolean }) {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs sm:text-sm" data-testid="demand-month-table">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1.5 pr-2 font-medium">Month</th>
            <th className="py-1.5 pr-2 font-medium">Peak day</th>
            {unlocked && <th className="py-1.5 pr-2 font-medium text-right">Peak</th>}
            <th className="py-1.5 font-medium text-right">Readings</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {series.months.map(m => {
            let best = series.days[m.start];
            let readings = 0;
            for (let i = m.start; i < m.end; i++) {
              readings += series.days[i].n;
              if (series.days[i].max > best.max) best = series.days[i];
            }
            const isPeak = best.date === series.peak.date;
            return (
              <tr key={m.key} className={isPeak ? 'bg-blue-50 font-semibold' : undefined}>
                <td className="py-1.5 pr-2 text-gray-900">{MONTHS[Number(m.key.slice(5, 7)) - 1]} {m.key.slice(0, 4)}</td>
                <td className="py-1.5 pr-2 text-gray-700">{formatDowMonthDay(best.date)}</td>
                {unlocked && <td className="py-1.5 pr-2 text-right tabular-nums text-gray-900">{best.max.toFixed(2)} kW</td>}
                <td className="py-1.5 text-right tabular-nums text-gray-700">{readings.toLocaleString()}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
